import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Lenis from 'lenis';
import { ThemeProvider, useTheme } from './components/ThemeProvider';
import { LanguageProvider } from './components/LanguageProvider';
import { GamerAuthProvider } from './components/GamerAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Loader from './Loader';
import Hero from './components/Home/Hero';
import TrustBar from './components/Home/TrustBar';
import ServicesSection from './components/Home/ServicesSection';
import PricingSection from './components/Home/PricingSection';
import PortfolioPreview from './components/Home/PortfolioPreview';
import ProcessSection from './components/Home/ProcessSection';

import TestimonialsSection from './components/Home/TestimonialsSection';
import BgmiEsportsPlatform from './components/Home/BgmiEsportsPlatform';
import ProtocolPage from './components/Home/ProtocolPage';
import BgmiHero from './components/Home/BgmiHero';

import PortfolioPage from './components/PortfolioPage';
import GameswapHome from './components/GameswapHome';
import GamerProfile from './components/GamerProfile';
import BgmiProfile from './components/BgmiProfile';
import TournamentSlots from './components/Home/TournamentSlots';
import TournamentResult from './components/Home/TournamentResult';

import BypassPage from './components/BypassPage';
import FindTeamPage from './components/FindTeamPage';
import MarketplacePage from './components/MarketplacePage';
import AdminPanel from './components/AdminPanel';

function AppContent({ isGameswap, route }) {
  const { ready, resetReady } = useTheme();
  const isFirstMount = useRef(true);

  useLayoutEffect(() => {
    if (ready) {
      resetReady();
    }
  }, [isGameswap, resetReady]);

  const [activeTournament, setActiveTournament] = useState(null);
  const [activeResult, setActiveResult] = useState(null);

  useEffect(() => {
    const handleOpenSlots = (e) => {
      setActiveTournament(e.detail);
      window.scrollTo(0, 0);
    };
    const handleOpenResult = (e) => {
      setActiveResult(e.detail);
      window.scrollTo(0, 0);
    };
    window.addEventListener('openTournamentSlots', handleOpenSlots);
    window.addEventListener('openTournamentResult', handleOpenResult);
    return () => {
      window.removeEventListener('openTournamentSlots', handleOpenSlots);
      window.removeEventListener('openTournamentResult', handleOpenResult);
    };
  }, []);

  useEffect(() => {
    isFirstMount.current = false;
  }, []);



  return (
    <div className="block">
      <style>{`
        @keyframes stayOpaque {
            from { opacity: 1; }
            to { opacity: 1; }
        }
        @keyframes circleExpand {
            from {
                clip-path: circle(0% at 50% 50%);
            }
            to {
                clip-path: circle(150% at 50% 50%);
            }
        }
        ::view-transition-group(root) {
            animation-duration: 0.5s;
        }
        ::view-transition-new(root) {
            animation-name: circleExpand;
        }
        ::view-transition-old(root) {
            animation-name: stayOpaque;
        }
      `}</style>
      <Loader key={isGameswap ? 'gamer' : 'bgmi'} mode={isGameswap ? 'GAMER' : 'BGMI'} isInitial={isFirstMount.current} />
      <div style={{ opacity: ready ? 1 : 0, transition: 'none', pointerEvents: ready ? 'auto' : 'none' }}>
        {activeResult ? (
          <TournamentResult 
            tournament={activeResult} 
            onClose={() => setActiveResult(null)} 
          />
        ) : activeTournament ? (
          <TournamentSlots 
            tournament={activeTournament} 
            onClose={() => setActiveTournament(null)} 
          />
        ) : (
          <>
            {!['#terms-of-engagement', '#privacy-directives', '#payout-architecture', '#disciplinary-policy', '#admin'].includes(route) && <Header />}
            <main>
              {route === '#projects' ? (
                <PortfolioPage />
              ) : route === '#profile' ? (
                isGameswap ? <GamerProfile /> : <BgmiProfile />
              ) : route === '#bypass' ? (
                <BypassPage />
              ) : route === '#recruit-zone' ? (
                <FindTeamPage />
              ) : route === '#admin' ? (
                <AdminPanel />
              ) : route === '#terms-of-engagement' ? (
                <ProtocolPage 
                    title="Terms of Engagement" 
                    content={
                        <>
                            <h3>1. Comprehensive Acceptance of Terms</h3>
                            <p>By accessing, browsing, or registering on the GridLabs Esports platform, you unconditionally agree to abide by these terms of engagement. These rules are designed to ensure fair play, competitive integrity, and a safe environment for all participants. Failure to comply with any section of this agreement may result in immediate suspension, disqualification from ongoing tournaments, or a permanent ban from all related GridLabs services.</p>
                            
                            <h3>2. Player Eligibility and Verification</h3>
                            <p>All participants must meet the minimum age requirement (16+ years) as stated by the tournament organizers and local laws. GridLabs reserves the right to request valid government-issued identification at any point, specifically prior to major prize pool distributions. Any falsification of age, identity, or geographic location will result in an immediate and irreversible ban.</p>
                            
                            <h3>3. Code of Conduct and Sportsmanship</h3>
                            <p>We maintain a strict, zero-tolerance policy for toxic behavior. This includes, but is not limited to, harassment, hate speech, discrimination, doxxing, or verbal abuse targeted at other players, tournament admins, or staff members. Players must demonstrate utmost sportsmanship and professional conduct across all platform-related communications, including in-game chat, official Discord servers, and social media interactions.</p>
                            
                            <h3>4. Account Sharing and Smurfing Policies</h3>
                            <p>Account sharing is strictly prohibited under all circumstances. Each participant must use their own verified, primary BGMI account. Suspected "smurfing" (the act of an experienced player using a lower-ranked or alternate account to bypass skill-based matchmaking) will lead to immediate disqualification of the entire team and forfeiture of all accumulated GLPC.</p>

                            <h3>5. Intellectual Property Rights</h3>
                            <p>All content generated during tournaments, including but not limited to gameplay footage, screenshots, chat logs, and tournament brackets, remains the intellectual property of GridLabs Esports. By participating, you grant us the royalty-free right to use, broadcast, and distribute this media for promotional and administrative purposes.</p>

                            <h3>6. Dispute Resolution and Liability</h3>
                            <p>In the event of a dispute regarding tournament outcomes or rule violations, the decision of the GridLabs Administration Team is final and binding. GridLabs is not liable for any technical failures, internet outages, or hardware issues experienced by the user that may affect their tournament performance.</p>
                        </>
                    }
                />
              ) : route === '#privacy-directives' ? (
                <ProtocolPage 
                    title="Privacy Directives" 
                    content={
                        <>
                            <h3>1. Extensive Data Collection Mechanisms</h3>
                            <p>To provide a seamless esports experience, we collect essential personal and gaming information. This includes your Full Name, Game UIDs, Discord IDs, WhatsApp Numbers, Email Addresses, and detailed in-game performance statistics. We may also collect device information and IP addresses to enforce anti-cheat measures and ensure fair matchmaking.</p>
                            
                            <h3>2. Purpose and Utilization of Data</h3>
                            <p>Your data is exclusively utilized for managing complex tournament brackets, maintaining accurate leaderboards, verifying match results, processing secure financial payouts, and preventing fraudulent activities. Your performance data may be anonymized and aggregated for statistical analysis to improve platform features.</p>
                            
                            <h3>3. Strict Third-Party Sharing Protocols</h3>
                            <p>We absolutely do not sell, rent, or trade your personal information with external advertising agencies. Data is only shared with trusted third parties when explicitly necessary, such as payment gateways for processing your withdrawals, or legal authorities if required to comply with binding legal requests or to protect the safety of our users.</p>
                            
                            <h3>4. Advanced Data Security Infrastructure</h3>
                            <p>GridLabs employs state-of-the-art encryption (AES-256) and secure HTTPS protocols to protect your sensitive account details and transaction history. While we implement enterprise-grade security measures, participants are ultimately responsible for maintaining the confidentiality of their login credentials and employing strong, unique passwords.</p>

                            <h3>5. User Rights and Data Deletion</h3>
                            <p>Under applicable data protection laws, you retain the right to access, modify, or request the complete deletion of your personal data from our servers. Note that deleting your data will result in the permanent closure of your GridLabs account and the forfeiture of any pending GLPC or tournament placements. Requests can be initiated via our official support channels.</p>
                        </>
                    }
                />
              ) : route === '#payout-architecture' ? (
                <ProtocolPage 
                    title="Payout Architecture" 
                    content={
                        <>
                            <h3>1. Transparent Distribution Model</h3>
                            <p>Our financial model is built on absolute transparency. Exactly 85% of all total entry collections for any given tournament are directly injected into the prize pool. The remaining 15% covers server hosting, anti-cheat infrastructure, payment gateway fees, and administrative costs. Prize pools are dynamically calculated and instantly updated upon match completion.</p>
                            
                            <h3>2. The GLPC Financial System</h3>
                            <p>All tournament winnings, referral bonuses, and refunds are instantly credited to your secure internal wallet as GLPC. 1 GLPC is strictly equivalent to ₹ 1.25 INR. This localized virtual currency system ensures immediate crediting post-match verification, bypassing traditional banking delays during off-hours.</p>
                            
                            <h3>3. Comprehensive Withdrawal Mechanics</h3>
                            <p>To ensure financial sustainability and reduce microscopic transactions, a minimum balance of ₹ 300 is required to initiate any withdrawal request. Users can withdraw funds directly to their verified UPI IDs or linked Bank Accounts. Standard withdrawals are processed manually by our finance team within 24 to 48 business hours.</p>
                            
                            <h3>4. Tax Implications and Transparent Deductions</h3>
                            <p>GridLabs believes in zero hidden fees. However, in compliance with government regulations (such as TDS under section 194BA of the Income Tax Act for Indian residents), applicable taxes on net winnings may be deducted at the source. Any platform processing fees charged by external payment gateways will be clearly highlighted before you confirm your withdrawal.</p>

                            <h3>5. Audit and Verification Protocols</h3>
                            <p>Before processing large payouts, our administration conducts a thorough audit of the user's recent match history to verify the legitimacy of their winnings. Any account flagged by our automated anti-cheat system will have their payouts frozen pending a manual review.</p>
                        </>
                    }
                />
              ) : route === '#disciplinary-policy' ? (
                <ProtocolPage 
                    title="Disciplinary Policy" 
                    content={
                        <>
                            <h3>1. Draconian Anti-Cheat Enforcement</h3>
                            <p>The use of unauthorized third-party software, aimbots, wallhacks, radar hacks, recoil macros, or unauthorized emulators (in mobile-exclusive lobbies) is an unforgivable offense. Detection by our anti-cheat algorithms or manual review will lead to an immediate, permanent ban spanning your IP address, device hardware ID, and all associated accounts.</p>
                            
                            <h3>2. Prohibition of Teaming and Collusion</h3>
                            <p>Competitive integrity is our highest priority. Teaming with opposing players, stream sniping live broadcasters, intentionally feeding kills, or any other form of match-fixing will result in immediate disqualification of all involved parties. Confirmed collusion will result in a minimum 6-month platform ban and total forfeiture of wallet balances.</p>
                            
                            <h3>3. Hardware Liability and Match Disconnects</h3>
                            <p>Players are entirely responsible for their own hardware performance, battery levels, and internet connection stability. Official matches will under no circumstances be paused, restarted, or rescheduled due to individual player disconnects, crashes, or lag. Rematches are only issued if the game servers experience a verified global crash affecting over 40% of the lobby.</p>
                            
                            <h3>4. Rigorous Reporting and Appeal Procedures</h3>
                            <p>If you suspect foul play, you must submit a formal report accompanied by clear, unedited video evidence within 2 hours of the match's conclusion. Reports lacking video evidence will be discarded. Users who receive a ban may submit exactly one appeal ticket; the subsequent decision made by the Senior Admin team is absolute, final, and binding.</p>
                            
                            <h3>5. Exploitation of Platform Bugs</h3>
                            <p>Intentionally exploiting bugs, glitches, or loopholes within the GridLabs platform (such as duplicating GLPC, bypassing entry fees, or manipulating matchmaking) is considered digital theft. Violators will face permanent bans and potential legal action to recover lost assets.</p>
                        </>
                    }
                />
              ) : (
                <>
                  {isGameswap ? (
                      <>
                          <GameswapHome isBgmi={false} />
                          <PricingSection isGameswap={true} />
                      </>
                  ) : (
                      <>
                          <BgmiHero />
                          <BgmiEsportsPlatform />
                      </>
                  )}
                </>
              )}
            </main>
            {isGameswap && <Footer isGameswap={isGameswap} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [route, setRoute] = useState(window.location.hash || '#');

  useEffect(() => {
    const onHashChange = () => setRoute(window.location.hash || '#');
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [isGameswap, setIsGameswap] = useState(window.location.pathname.includes('gameswap'));

  useEffect(() => {
    const handleToggle = (e) => {
      setIsGameswap(e.detail);
      window.scrollTo(0, 0);
    };
    window.addEventListener('gameswapToggle', handleToggle);
    return () => window.removeEventListener('gameswapToggle', handleToggle);
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      document.documentElement.classList.add('is-scrolling');
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        document.documentElement.classList.remove('is-scrolling');
      }, 800);
    };

    const handleMouseMove = (e) => {
      if (window.innerWidth - e.clientX < 20) {
        document.documentElement.classList.add('is-scrolling');
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          document.documentElement.classList.remove('is-scrolling');
        }, 800);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(scrollTimeout);
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.6,
      easing: (t) => 1 - Math.pow(1 - t, 3), // quick, non-magnetic cubic-out
      smoothWheel: true,
      wheelMultiplier: 1.2,
      touchMultiplier: 2,
    });
    window.lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    const onHashChange = () => {
      setRoute(window.location.hash || '#');
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('hashchange', onHashChange);
      lenis.destroy();
    };
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <GamerAuthProvider>
          <AppContent isGameswap={isGameswap} route={route} />
        </GamerAuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}


